import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { isAxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ApiError, CustomUserCreatePasswordRetype } from 'api';
import { authAction } from 'common/actions';
import {
  AUTH_EMAIL_PARAM,
  AUTH_RETURN_TO_PARAM,
  AUTH_SOURCE_PARAM,
  AUTH_STORAGE_PERSISTENCE_LOCAL,
  AUTH_VIEW_PARAM,
  SIGN_IN_VIEW,
  SIGN_UP_VIEW,
  SignUpView,
} from 'common/constants/auth';
import { useStore } from 'common/store';
import { Alert } from 'components/ui';
import { AuthFooterPrompt, AuthFormHeader, AuthPageStack, AuthResendLabel, AuthTitleText } from 'pages/Auth/components';
import {
  useAuthResendCooldown,
  useAuthSession,
  useAuthSocialSignIn,
  useAuthViewTransition,
  useClearFieldErrorOnValueChange,
  useCreateUser,
  useGetSocialAccessToken,
  useResendActivationEmail,
  useRevalidateFieldOnDependencyChange,
  useTurnstileToken,
} from 'pages/Auth/hooks';
import {
  buildAuthSearchParams,
  buildSignInUrl,
  clearFieldErrorsByType,
  replaceSearchParamsIfChanged,
} from 'pages/Auth/utils';
import { TurnstileError } from 'pages/Auth/utils/turnstile/turnstile';
import { SignUpConfirmationStep, SignUpFormStep } from './components';
import {
  SIGN_UP_FOOTER_PROMPT,
  SIGN_UP_FOOTER_SIGN_IN_LABEL,
  SIGN_UP_RESEND_ACTION_LABEL,
  SIGN_UP_RESEND_FAILURE_MESSAGE,
  SIGN_UP_RESEND_MISSING_EMAIL_ERROR,
  SIGN_UP_RESEND_SUCCESS_MESSAGE,
  SIGN_UP_SUBMIT_ERROR,
  SIGN_UP_TITLE_LEAD,
  SIGN_UP_TITLE_TAIL,
  SIGN_UP_TURNSTILE_ERROR,
} from './SignUp.constants';
import { SIGN_UP_SCHEMA } from './SignUp.schema';
import { SignUpFormValues } from './SignUp.types';

const getSignUpView = (value: string | null): SignUpView =>
  value === SIGN_UP_VIEW.CONFIRMATION ? SIGN_UP_VIEW.CONFIRMATION : SIGN_UP_VIEW.FORM;

const signUpHeaderTitle = <AuthTitleText lead={SIGN_UP_TITLE_LEAD} tail={SIGN_UP_TITLE_TAIL} />;

const SignUp = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const createUser = useCreateUser({ useGlobalLoading: false });
  const getSocialAccessToken = useGetSocialAccessToken({ useGlobalLoading: false });
  const resendActivationEmail = useResendActivationEmail({ useGlobalLoading: false });
  const turnstileHostRef = useRef<HTMLDivElement | null>(null);
  const { withToken: withTurnstileToken } = useTurnstileToken('signup', turnstileHostRef);
  const [{ user }, dispatch] = useStore();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState<{ severity: 'error' | 'success'; text: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const { isCoolingDown, remainingSeconds, startCooldown } = useAuthResendCooldown();
  const { hasSession } = useAuthSession();
  const view = getSignUpView(searchParams.get(AUTH_VIEW_PARAM));
  const confirmationEmail = searchParams.get(AUTH_EMAIL_PARAM) ?? '';
  const authSource = searchParams.get(AUTH_SOURCE_PARAM);
  const authReturnTo = searchParams.get(AUTH_RETURN_TO_PARAM);
  const { isViewTransitionPending, startViewTransition } = useAuthViewTransition(view);
  const isFormBusy = isSubmitting || isViewTransitionPending;
  const resendActionLabel = (
    <AuthResendLabel label={SIGN_UP_RESEND_ACTION_LABEL} remainingSeconds={isCoolingDown ? remainingSeconds : undefined} />
  );
  const signInHref = useMemo(
    () =>
      buildSignInUrl({
        returnTo: authReturnTo,
        source: authSource,
      }),
    [authReturnTo, authSource]
  );
  const confirmationSignInHref = useMemo(
    () =>
      buildSignInUrl({
        returnTo: authReturnTo,
        source: authSource,
        view: SIGN_IN_VIEW.IDENTIFIER,
      }),
    [authReturnTo, authSource]
  );
  const {
    handleGoogleLogin,
    handleMicrosoftLogin,
    isSocialPending,
    socialProviderPending,
  } = useAuthSocialSignIn({
    getSocialAccessToken,
    persistence: AUTH_STORAGE_PERSISTENCE_LOCAL,
    setAuthError: setSubmitError,
  });

  const form = useForm<SignUpFormValues>({
    defaultValues: {
      email: confirmationEmail,
      first_name: '',
      last_name: '',
      password: '',
      re_password: '',
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(SIGN_UP_SCHEMA),
  });
  const [watchedEmail, watchedFirstName, watchedLastName, watchedPassword, watchedPasswordConfirmation] = form.watch([
    'email',
    'first_name',
    'last_name',
    'password',
    're_password',
  ]);
  useRevalidateFieldOnDependencyChange({
    dependencies: [watchedPassword],
    fieldName: 're_password',
    form,
  });

  const clearAlertFieldErrors = useCallback(() => {
    clearFieldErrorsByType(form, ['email', 'first_name', 'last_name', 'password', 're_password'], ['alert']);
  }, [form]);

  const dismissSubmitError = useCallback(() => {
    setSubmitError(null);
    clearAlertFieldErrors();
  }, [clearAlertFieldErrors]);

  const hasLoggedInUser = useMemo(() => hasSession && Boolean(user && Object.keys(user).length), [hasSession, user]);

  // Only the confirmation state survives refresh; editable form values should not keep submitted PII in the URL.
  useEffect(() => {
    const shouldStayOnConfirmation = view === SIGN_UP_VIEW.CONFIRMATION && Boolean(confirmationEmail);
    const nextParams = buildAuthSearchParams({
      email: shouldStayOnConfirmation ? confirmationEmail : null,
      returnTo: authReturnTo,
      source: authSource,
      view: shouldStayOnConfirmation ? SIGN_UP_VIEW.CONFIRMATION : null,
    });

    replaceSearchParamsIfChanged(searchParams, nextParams, params => {
      setSearchParams(params, { replace: true });
    });
  }, [authReturnTo, authSource, confirmationEmail, searchParams, setSearchParams, view]);

  useClearFieldErrorOnValueChange({
    errorTypes: ['alert'],
    fieldNames: ['email', 'first_name', 'last_name', 'password', 're_password'],
    form,
    onClear: dismissSubmitError,
    shouldClear: () => Boolean(submitError),
    values: [watchedEmail, watchedFirstName, watchedLastName, watchedPassword, watchedPasswordConfirmation],
  });

  if (hasLoggedInUser) {
    return <Navigate replace to={buildSignInUrl({ view: SIGN_IN_VIEW.WORKSPACE_SELECTION })} />;
  }

  // Sign-up only owns the confirmation step in the URL; returning to the form always drops previously submitted email state.
  const replaceViewState = (nextView: SignUpView, email?: string) => {
    const nextParams = buildAuthSearchParams({
      email: nextView === SIGN_UP_VIEW.CONFIRMATION ? email ?? null : null,
      returnTo: authReturnTo,
      source: authSource,
      view: nextView === SIGN_UP_VIEW.CONFIRMATION ? nextView : null,
    });

    startTransition(() => {
      setSearchParams(nextParams, { replace: true });
    });
  };

  const getServerErrorBody = (error: unknown): Record<string, unknown> | null => {
    // OpenAPI request() rejects with Axios errors (not ApiError). Support both shapes.
    if (error instanceof ApiError && error.body && typeof error.body === 'object') {
      return error.body as Record<string, unknown>;
    }
    if (isAxiosError(error) && error.response?.data && typeof error.response.data === 'object') {
      return error.response.data as Record<string, unknown>;
    }
    return null;
  };

  const applyServerFieldErrors = (body: Record<string, unknown>) => {
    const fieldNames: Array<keyof SignUpFormValues> = ['email', 'first_name', 'last_name', 'password', 're_password'];
    let hasFieldErrors = false;
    let firstFieldError: keyof SignUpFormValues | null = null;

    fieldNames.forEach(fieldName => {
      const fieldError = body[fieldName];
      const firstMessage = Array.isArray(fieldError) ? fieldError[0] : undefined;

      if (typeof firstMessage === 'string') {
        hasFieldErrors = true;
        firstFieldError ??= fieldName;
        form.setError(fieldName, { message: firstMessage, type: 'server' });
      }
    });

    if (firstFieldError) {
      form.setFocus(firstFieldError);
    }

    return hasFieldErrors;
  };

  const handleSubmit = form.handleSubmit(async values => {
    if (isSubmitting) {
      return;
    }

    clearAlertFieldErrors();
    setSubmitError(null);
    setConfirmationMessage(null);
    setIsSubmitting(true);

    const payload: CustomUserCreatePasswordRetype = {
      email: values.email.trim(),
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      password: values.password,
      re_password: values.re_password,
    };

    let createdUser: CustomUserCreatePasswordRetype | null;
    let error: unknown;
    try {
      [createdUser, error] = await withTurnstileToken(token =>
        createUser({
          ...payload,
          ...(token ? { turnstile_token: token } : {}),
        })
      );
    } catch (submitFailure) {
      setSubmitError(submitFailure instanceof TurnstileError ? SIGN_UP_TURNSTILE_ERROR : SIGN_UP_SUBMIT_ERROR);
      setIsSubmitting(false);
      return;
    }

    if (error) {
      const body = getServerErrorBody(error);
      if (body) {
        const hasFieldErrors = applyServerFieldErrors(body);
        if (Object.prototype.hasOwnProperty.call(body, 'turnstile_token')) {
          setSubmitError(SIGN_UP_TURNSTILE_ERROR);
        } else {
          setSubmitError(hasFieldErrors ? null : SIGN_UP_SUBMIT_ERROR);
        }
        setIsSubmitting(false);
        return;
      }

      setSubmitError(SIGN_UP_SUBMIT_ERROR);
      setIsSubmitting(false);
      return;
    }

    dispatch(authAction.setActivationEmail(createdUser?.email ?? payload.email));
    startCooldown();
    startViewTransition(SIGN_UP_VIEW.CONFIRMATION);
    replaceViewState(SIGN_UP_VIEW.CONFIRMATION, createdUser?.email ?? payload.email);
    setIsSubmitting(false);
  });

  const handleResendActivation = async () => {
    if (isResending) {
      return;
    }

    if (!confirmationEmail) {
      setConfirmationMessage({
        severity: 'error',
        text: SIGN_UP_RESEND_MISSING_EMAIL_ERROR,
      });
      return;
    }

    setIsResending(true);
    setConfirmationMessage(null);

    const [, error] = await resendActivationEmail(confirmationEmail);

    if (error) {
      setConfirmationMessage({
        severity: 'error',
        text: SIGN_UP_RESEND_FAILURE_MESSAGE,
      });
    } else {
      startCooldown();
      setConfirmationMessage({
        severity: 'success',
        text: SIGN_UP_RESEND_SUCCESS_MESSAGE,
      });
    }

    setIsResending(false);
  };

  return (
    <AuthPageStack>
      <AuthFormHeader title={signUpHeaderTitle} />

      {submitError ? (
        <Alert onClose={dismissSubmitError} severity="error" visualVariant="inlineError">
          {submitError}
        </Alert>
      ) : null}
      {confirmationMessage ? (
        <Alert
          onClose={() => setConfirmationMessage(null)}
          severity={confirmationMessage.severity}
          visualVariant="inline"
        >
          {confirmationMessage.text}
        </Alert>
      ) : null}

      {view === SIGN_UP_VIEW.FORM ? (
        <SignUpFormStep
          form={form}
          isSocialPending={isSocialPending}
          isSubmitting={isFormBusy}
          onGoogleLogin={handleGoogleLogin}
          onMicrosoftLogin={handleMicrosoftLogin}
          onSubmit={handleSubmit}
          socialProviderPending={socialProviderPending}
          turnstileHostRef={turnstileHostRef}
        />
      ) : (
        <SignUpConfirmationStep
          isResendDisabled={isCoolingDown}
          isResending={isResending}
          onResendActivation={handleResendActivation}
          resendActionLabel={resendActionLabel}
          signInHref={confirmationSignInHref}
        />
      )}

      {view === SIGN_UP_VIEW.FORM ? (
        <AuthFooterPrompt
          disabled={isFormBusy}
          linkLabel={SIGN_UP_FOOTER_SIGN_IN_LABEL}
          prompt={SIGN_UP_FOOTER_PROMPT}
          to={signInHref}
        />
      ) : null}
    </AuthPageStack>
  );
};

export default SignUp;
